import React, { useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { formatCurrency } from "utils/HelperFunctions";

ChartJS.register(ArcElement, Tooltip, Legend);

interface BudgetChartProps {
  incomeValues: { [key: string]: number };
  expenseValues: { [key: string]: number };
  income: any;
  expenses: any;
}

const BudgetChart: React.FC<BudgetChartProps> = ({
  incomeValues,
  expenseValues,
  income,
  expenses,
}) => {
  const incomeData = Object.values(incomeValues);
  const expenseData = Object.values(expenseValues);
  const outerData = {
    ...incomeValues,
    ...expenseValues,
  };
  const dataValues = [...incomeData, ...expenseData];
  const incomeTotal = incomeData.reduce((acc, value) => acc + value, 0);
  const expenseTotal = expenseData.reduce((acc, value) => acc + value, 0);

  let innerData = {
    Income: incomeTotal,
    Expenses: expenseTotal,
  };
  const incomeColors = ["#e6fdea", "#c0eec8"];
  const expenseColors = [
    "#fcdddd9C",
    "#fcdddd",
    "#ed8f8f73",
    "#ed8f8f99",
    "#ed8f8fBF",
    "#ed8f8f",
  ];

  const backgroundColor = [
    ...(incomeData.length > 0 ? incomeColors.slice(0, incomeData.length) : []),
    ...(expenseData.length > 0
      ? expenseColors.slice(0, expenseData.length)
      : []),
  ];

  const getTitle = (label: string, amount: any) => {
    switch (label) {
      case "Income/Expenses":
        return Object.entries(outerData).find(
          ([key, value]) => value === amount,
        )?.[0];
      case "Total":
        return Object.entries(innerData).find(
          ([key, value]) => value === amount,
        )?.[0];
      case "No Data":
        return "No Data";
      default:
        return label;
    }
  };

  const data = {
    datasets:
      dataValues.length > 0
        ? [
            {
              label: "Income/Expenses",
              labelValues: income,
              labelData: incomeData,
              data: dataValues,
              backgroundColor: backgroundColor,
              borderWidth: 8,
              spacing: 2,
              weight: 3,
            },
            {
              label: "Total",
              labelValues: expenses,
              labelData: expenseData,
              data: [incomeTotal, expenseTotal],
              backgroundColor: ["#6fc17d", "#f77171"],
              borderWidth: 8,
              spacing: 2,
              weight: 1.5,
            },
          ]
        : [
            {
              label: "No Data",
              data: [1],
              backgroundColor: ["#f3f4f8"],
              borderWidth: 8,
              spacing: 2,
              weight: 1,
            },
          ],
  };

  const options = {
    responsive: true,
    cutout: "45%",
    plugins: {
      tooltip: {
        enabled: true,
        callbacks: {
          title: (tooltipItems: any) => {
            const item = tooltipItems[0];
            let title = getTitle(item.dataset.label, item.raw);
            return title;
          },
          label: (tooltipItem: any) => {
            const value = tooltipItem.raw;
            return `${formatCurrency(value)}`;
          },
        },
        backgroundColor: "#fff",
        titleColor: "#19224C",
        labelColor: "#1678f2",
        padding: 8,
        bodyColor: "#1678f2",
        borderColor: "#b1bed2",
        borderRadius: 10,
        borderWidth: 1,
        boxShadow: "0px 10px 30px 0px #3C425726",
        titleFont: {
          size: 18,
          family: "DM Sans",
          weight: 700,
        },
        bodyFont: {
          size: 12,
          family: "DM Sans",
          weight: 500,
        },
      },
    },
    hover: {
      mode: null as any,
    },
  };

  return (
    <div className="xl:min-w-[344px] xl:min-h-[344px] h-full w-full max-w-[344px] max-h-[344px]">
      <Doughnut data={data} options={options} className="doughnut" />
    </div>
  );
};

export default BudgetChart;
